import {
    Dispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useRef,
} from 'react';

import { StreamTypes } from 'store/player/actions.types.ts';

import parseVideoItemForSO from 'services/data-manager/parsing/parseVideoItemForSO.ts';
import { VideoItem } from 'services/data-manager/types.ts';
import useGetPlaybackSkipBackUavParams from 'services/uav/useGetPlaybackSkipBackUavParams.ts';
import { logUavAnalytics } from 'services/uav/utils.ts';

import useUpdateEffect from 'utils/hooks/useUpdateEffect.ts';
import isNumber from 'utils/number.ts';

import { PlayerRef } from 'components/Player/types.ts';

import { StartoverStreamType, SwitchPlayerSource } from '../types.ts';
import {
    DEFAULT_SO_STREAM_DELAY_FROM_LIVE,
    isStartoverStreamStatic,
} from '../utils';
import { OnSkipHandler, SKIP_STEP } from './useSkippings.ts';
import {
    PlayerControlsAccessLevel,
    accessIsRestricted,
} from './useTrickModeControl.ts';

type Params = {
    playerRef: RefObject<PlayerRef | null>;
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    isLive: boolean;
    isCatchup: boolean;
    isStartover: boolean;
    isSOTSPauseDisabled: boolean;
    timeshiftAllowed: boolean;
    startoverAllowed: boolean;
    rewindAccessLevel: PlayerControlsAccessLevel;
    playerProgress: number;
    seeking: boolean;
    startoverStreamType: StartoverStreamType;
    playerPausedByUser: boolean;
    setPlayerProgress: Dispatch<SetStateAction<number>>;
    setReadyToPlay(ready: boolean): void;
    handleSkip: OnSkipHandler;
    onShowControlsRestrictedNotification(): void;
    onShowTimeshiftNotAvailableNotification(): void;
    onSwitchTimeshiftModeOn(progress: number): void;
    onSetTimeshiftNotification(): void;
    handleSwitchPlayerSource: SwitchPlayerSource;
};

const SKIP_DEBOUNCE_TIME = 400;

function useSkipBackward({
    playerRef,
    videoItem,
    isLive,
    isCatchup,
    isStartover,
    isSOTSPauseDisabled,
    timeshiftAllowed,
    startoverAllowed,
    rewindAccessLevel,
    playerProgress,
    seeking,
    startoverStreamType,
    playerPausedByUser,
    playbackType,
    setPlayerProgress,
    setReadyToPlay,
    handleSkip,
    onShowControlsRestrictedNotification,
    onShowTimeshiftNotAvailableNotification,
    onSwitchTimeshiftModeOn,
    onSetTimeshiftNotification,
    handleSwitchPlayerSource,
}: Params) {
    const { getPlaybackSkipBackUavParams } = useGetPlaybackSkipBackUavParams({
        playbackType,
        videoItem,
    });

    const skipInProgress = useRef(false);
    const skipBwdAttempts = useRef(0);
    const skipBwdTimeout = useRef<NodeJS.Timeout | null>(null);

    useUpdateEffect(() => {
        if (!seeking && skipInProgress.current && !playerPausedByUser) {
            playerRef.current?.play();
            skipInProgress.current = false;

            if (skipBwdTimeout.current != null) {
                clearTimeout(skipBwdTimeout.current);
                skipBwdTimeout.current = null;
            }
        }
    }, [seeking]);

    const handleSkipBackwardDynamicStartover = useCallback(() => {
        playerRef.current?.pause();
        skipInProgress.current = true;
        skipBwdAttempts.current += 1;
        setPlayerProgress((prevState) => {
            return Math.max(0, prevState - SKIP_STEP);
        });

        if (skipBwdTimeout.current != null) {
            clearTimeout(skipBwdTimeout.current);
            skipBwdTimeout.current = null;
        }

        skipBwdTimeout.current = setTimeout(async () => {
            const [currentTimeShift, maxTimeShift] = await Promise.all([
                playerRef.current?.getTimeShift(),
                playerRef.current?.getMaxTimeShift(),
            ]);

            if (isNumber(currentTimeShift) && isNumber(maxTimeShift)) {
                handleSkip({
                    timeShiftOffset: Math.max(
                        maxTimeShift,
                        currentTimeShift - skipBwdAttempts.current * SKIP_STEP,
                    ),
                });
            }

            skipBwdAttempts.current = 0;
        }, SKIP_DEBOUNCE_TIME);
    }, [playerRef, handleSkip, setPlayerProgress]);

    const handleSkipBackwardStaticStream = useCallback(() => {
        playerRef.current?.pause();
        skipInProgress.current = true;
        setPlayerProgress((prevState) => {
            return Math.max(0, prevState - SKIP_STEP);
        });

        if (skipBwdTimeout.current != null) {
            clearTimeout(skipBwdTimeout.current);
            skipBwdTimeout.current = null;
        }

        skipBwdTimeout.current = setTimeout(() => {
            handleSkip({
                seekPosition: Math.max(0, playerProgress - SKIP_STEP),
            });
        }, SKIP_DEBOUNCE_TIME);
    }, [handleSkip, playerProgress, setPlayerProgress]);

    const handleSkipBackwardLive = useCallback(() => {
        setReadyToPlay(false);

        if (timeshiftAllowed) {
            onSwitchTimeshiftModeOn(playerProgress);

            if (!startoverAllowed) {
                onSetTimeshiftNotification();
            }
        }

        if (!timeshiftAllowed && startoverAllowed && videoItem) {
            let sourceConfig: VideoItem['sourceConfig'];

            if (isStartoverStreamStatic(startoverStreamType)) {
                sourceConfig = {
                    startOffset: Math.max(
                        0,
                        playerProgress + (videoItem.guardTime || 0) - SKIP_STEP,
                    ),
                };
            } else {
                sourceConfig = {
                    startOffset: -(
                        SKIP_STEP + DEFAULT_SO_STREAM_DELAY_FROM_LIVE
                    ),
                };
            }

            handleSwitchPlayerSource(
                parseVideoItemForSO(videoItem, sourceConfig),
                StreamTypes.startover,
            );
        }
    }, [
        handleSwitchPlayerSource,
        onSetTimeshiftNotification,
        onSwitchTimeshiftModeOn,
        playerProgress,
        setReadyToPlay,
        startoverAllowed,
        startoverStreamType,
        timeshiftAllowed,
        videoItem,
    ]);

    return useCallback(async () => {
        if (!seeking) {
            if (accessIsRestricted(rewindAccessLevel)) {
                onShowControlsRestrictedNotification();
            } else if (isLive && isSOTSPauseDisabled) {
                onShowTimeshiftNotAvailableNotification();
            } else {
                const isStaroverDynamicStream =
                    (isStartover || isCatchup) &&
                    (await playerRef.current?.getIsLive());

                logUavAnalytics(
                    getPlaybackSkipBackUavParams({
                        startPosition: playerProgress,
                        endPosition: playerProgress - SKIP_STEP,
                    }),
                );

                if (isLive) {
                    handleSkipBackwardLive();
                } else if (isStaroverDynamicStream) {
                    handleSkipBackwardDynamicStartover();
                } else {
                    handleSkipBackwardStaticStream();
                }
            }
        }
    }, [
        seeking,
        rewindAccessLevel,
        isLive,
        isSOTSPauseDisabled,
        onShowControlsRestrictedNotification,
        onShowTimeshiftNotAvailableNotification,
        isStartover,
        isCatchup,
        playerRef,
        getPlaybackSkipBackUavParams,
        playerProgress,
        handleSkipBackwardLive,
        handleSkipBackwardDynamicStartover,
        handleSkipBackwardStaticStream,
    ]);
}

export default useSkipBackward;
